import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Ban, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WEEKDAYS } from "@/components/RecurringOptions";

export const activeRestriction = (m) => {
  const r = m?.booking_restriction;
  return r?.until && r.until >= format(new Date(), "yyyy-MM-dd") ? r : null;
};
const trDate = (d) => format(parseISO(d), "dd.MM.yyyy");

export const RestrictedBadge = ({ member }) => {
  const r = activeRestriction(member);
  if (!r) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-red-300 bg-red-50 text-red-700"
      title={`${trDate(r.until)} tarihine kadar online rezervasyon kısıtlı${r.note ? ` · ${r.note}` : ""}`} data-testid={`member-flag-restricted-${member.member_id}`}>
      <Ban className="w-3 h-3" />Kısıtlı
    </span>
  );
};

export const RestrictionWarning = ({ member }) => {
  const r = activeRestriction(member);
  if (!r) return null;
  return (
    <div className="mt-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-2.5 text-xs text-red-700" data-testid="member-restriction-warning">
      <Ban className="w-4 h-4 shrink-0" />
      <span>Bu üyenin <b>{trDate(r.until)}</b> tarihine kadar online rezervasyon kısıtlaması var{r.note ? ` (${r.note})` : ""}. Panelden rezervasyon yapabilirsiniz.</span>
    </div>
  );
};

const call = async (url, method, body) => {
  const res = await fetch(url, { method, credentials: "include", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "İşlem başarısız");
  return data;
};

export const RestrictionPanel = ({ member, onChanged }) => {
  const [days, setDays] = useState("5");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const r = activeRestriction(member);
  const run = async (method, body) => {
    setBusy(true);
    try {
      const d = await call(`${API}/members/${member.member_id}/booking-restriction`, method, body);
      toast.success(method === "PUT" ? "Online rezervasyon kısıtlandı" : "Kısıtlama kaldırıldı");
      onChanged({ ...member, booking_restriction: d.booking_restriction });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={`p-4 rounded-xl border ${r ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`} data-testid="member-restriction-panel">
      <p className="text-sm font-semibold text-navy flex items-center gap-2 mb-2"><Ban className="w-4 h-4 text-red-600" />Online Rezervasyon Kısıtlaması</p>
      {r ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-red-700" data-testid="member-restriction-status"><b>{trDate(r.until)}</b> tarihine kadar kısıtlı ({r.days} gün){r.note ? ` · ${r.note}` : ""}</p>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => run("DELETE")} className="rounded-lg" data-testid="member-restriction-remove"><RotateCcw className="w-3.5 h-3.5 mr-1" />Kısıtlamayı Kaldır</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-24"><p className="text-xs text-grey mb-1">Gün sayısı</p><Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} className="h-9 rounded-lg" data-testid="member-restriction-days" /></div>
          <div className="flex-1 min-w-[160px]"><p className="text-xs text-grey mb-1">Not (isteğe bağlı)</p><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. sık iptal" className="h-9 rounded-lg" data-testid="member-restriction-note" /></div>
          <Button size="sm" disabled={busy || !(Number(days) >= 1)} onClick={() => run("PUT", { days: Number(days), note })} className="h-9 rounded-lg bg-red-600 hover:bg-red-700" data-testid="member-restriction-submit">Kısıtla</Button>
        </div>
      )}
    </div>
  );
};

export const templateLabel = (tpl) => `${WEEKDAYS.filter((w) => tpl.days.includes(w.d)).map((w) => w.l).join("-")} ${tpl.start_time}-${tpl.end_time}`;

export const RecurringTemplateInfo = ({ member, onChanged }) => {
  const tpl = member?.recurring_template;
  if (!tpl?.days?.length) return null;
  const remove = async () => {
    try { await call(`${API}/members/${member.member_id}/recurring-template`, "DELETE"); toast.success("Kayıtlı düzen silindi"); onChanged({ ...member, recurring_template: null }); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="p-4 rounded-xl border border-purple-200 bg-purple-50 flex items-center justify-between gap-2" data-testid="member-recurring-template">
      <p className="text-sm text-purple-900"><b>Kayıtlı düzen:</b> {templateLabel(tpl)}</p>
      <Button size="sm" variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700" data-testid="member-recurring-template-delete"><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
};

export const RecurringTemplateBar = ({ member, current, onApply, onSaved }) => {
  const [busy, setBusy] = useState(false);
  if (!member) return null;
  const tpl = member.recurring_template;
  const canSave = current.days.length > 0 && !!current.start_time && !!current.end_time;
  const save = async () => {
    setBusy(true);
    try {
      const d = await call(`${API}/members/${member.member_id}/recurring-template`, "PUT", current);
      toast.success("Düzen üyeye kaydedildi");
      onSaved({ ...member, recurring_template: d.recurring_template });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="recurring-template-bar">
      {!tpl?.days?.length && <span className="text-purple-400" data-testid="recurring-template-none">Kayıtlı düzen yok</span>}
      {tpl?.days?.length > 0 && (
        <span className="inline-flex items-center gap-2 rounded-lg bg-white border border-purple-200 px-2 py-1 text-purple-900" data-testid="recurring-template-saved">
          Kayıtlı düzen: <b>{templateLabel(tpl)}</b>
          <button type="button" onClick={() => onApply(tpl)} className="font-semibold text-purple-700 underline" data-testid="recurring-template-apply">Uygula</button>
        </span>
      )}
      {canSave && (
        <button type="button" disabled={busy} onClick={save} className="font-semibold text-purple-700 hover:underline disabled:opacity-50" data-testid="recurring-template-save">
          {tpl?.days?.length ? "Bu düzenle güncelle" : "Bu düzeni üyeye kaydet"}
        </button>
      )}
    </div>
  );
};
